import { ALL_NAV, ownsPath, type StudioNavDefinition } from './navConfig';

/**
 * Capability authorization for studio modules.
 *
 * `canAccessNavItem` and `filterNavItemsByCapabilities` already existed and already answered the
 * sidebar's question. `capabilityForPath` extends the same module to answer the ROUTE's question from
 * the same definitions, rather than a second permission system growing beside this one.
 *
 * THIS HIDES A LINK **AND NOW GUARDS A ROUTE** — but it is still not the last line of defence. The
 * backend proxy authorises every module call and returns 403 regardless of what the client believes.
 * That has to stay true: a capability list is data the client received, and a client cannot be trusted
 * to police itself. What this buys is that an unauthorized module is never mounted, never fetches, and
 * says why — instead of loading, firing requests and collapsing into error states.
 */

export interface CapabilityGatedItem {
  moduleKey?: string;
}

export function canAccessNavItem(item: CapabilityGatedItem, capabilityKeys: string[]): boolean {
  return !item.moduleKey || capabilityKeys.includes(item.moduleKey);
}

export function filterNavItemsByCapabilities<T extends CapabilityGatedItem>(
  items: T[],
  capabilityKeys: string[]
): T[] {
  return items.filter((item) => canAccessNavItem(item, capabilityKeys));
}

/**
 * The capability a path requires, or `null` when the path is ungated.
 *
 * Longest match wins. `/agents/new` and `/agents` both exist, and `/studio/org-admin` sits under no
 * gated item while `/studio/git` does — resolving by declaration order instead would make the answer
 * depend on how the arrays happen to be written.
 *
 * `null` means "no capability required", which is the honest answer for Home, Deployment,
 * Observability, Connection Manager and Migrations: none of them carries a `moduleKey` today. It must
 * NOT be read as "deny" — turning unlisted paths into denials would lock users out of every screen
 * this map does not mention, which is most of the app.
 */
export function capabilityForPath(pathname: string): string | null {
  let best: StudioNavDefinition | null = null;
  let bestLength = -1;

  for (const item of ALL_NAV) {
    if (!item.moduleKey || !ownsPath(item, pathname)) continue;
    // Compare on the matched prefix, not the href: `/reports` owns `/agent-runs`, and that match is
    // more specific than a shorter href that also happens to match.
    if (item.href.length > bestLength) {
      best = item;
      bestLength = item.href.length;
    }
  }

  return best?.moduleKey ?? null;
}

/** Whether `capabilityKeys` permits `pathname`. Ungated paths are always permitted. */
export function canAccessPath(pathname: string, capabilityKeys: string[]): boolean {
  const required = capabilityForPath(pathname);
  return required === null || capabilityKeys.includes(required);
}
